<script setup lang="ts">
import type { VariantProps } from 'tailwind-variants';
import type { HTMLAttributes } from 'vue';
import type { ThemeSlots } from '#layers/ui/types/tv';
import { tv } from 'tailwind-variants';
import { computed } from 'vue';
import { getBorderColor, getContrastRatio } from '#layers/ui/utils/colors';

interface ColorDotProps {
	backgroundColor?: string;
	color?: string;
	size?: ColorDotVariants['size'];
	ui?: ThemeSlots<typeof colorDot>;
	class?: HTMLAttributes['class'];
}

const { backgroundColor = 'var(--ui-bg)', color, size, class: className } = defineProps<ColorDotProps>();

const colorDot = tv({
	slots: {
		base: 'inline-flex items-center',
		dot: 'inline-block shrink-0 rounded-full',
	},
	variants: {
		size: {
			xs: { dot: 'size-2' },
			sm: { dot: 'size-3' },
			md: { dot: 'size-4' },
			lg: { dot: 'size-5' },
			xl: { dot: 'size-6' },
		},
		hasBorder: {
			true: { dot: 'ring-1 ring-inset' },
			false: {},
		},
	},
	defaultVariants: {
		size: 'md',
		hasBorder: false,
	},
});

type ColorDotVariants = VariantProps<typeof colorDot>;

const resolvedBackgroundColor = computed(() =>
	backgroundColor.startsWith('var(')
		? getComputedStyle(document.body).getPropertyValue(backgroundColor.slice(4, -1).trim())
		: backgroundColor,
);

const shouldShowBorder = computed(() => {
	if (!color) return false;

	try {
		const backgroundColor = resolvedBackgroundColor.value;
		const contrastRatio = getContrastRatio(color, backgroundColor);
		return contrastRatio < 3;
	}
	catch {
		return true;
	}
});

const borderColor = computed(() => {
	if (!color || !shouldShowBorder.value) return;

	return getBorderColor(color) ?? 'var(--ui-border)';
});

const theme = computed(() => colorDot({
	size,
	hasBorder: shouldShowBorder.value,
}));
</script>

<template>
	<div :class="theme.base({ class: ui?.base })" aria-hidden="true">
		<div
			class="bg-(--color-dot-color)"
			:class="[
				theme.dot({ class: ui?.dot }),
				className,
			]"
			:style="{ '--color-dot-color': color, '--tw-ring-color': borderColor }"
		/>
	</div>
</template>
